import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useBroadcastDeviceVodList, BroadcastDeviceVod } from '@/hooks/useBroadcastDeviceVodList'
import { VodDialog } from './VodDialog'
import { broadcastDeviceService } from '@/services/broadcast_device_service'
import { useAuth } from '@/hooks/useAuth'
import { Badge } from '@/components/ui/badge'
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { BroadcastDeviceVodType } from '@/data/broadcastDeviceVodType'

interface VodListProps {
  type: BroadcastDeviceVodType
  broadcastDeviceId?: number
  onSuccess?: () => void
  templateName?: string | null
  templateId?: number | null
}

export function VodList({ type, broadcastDeviceId, onSuccess, templateName, templateId }: VodListProps) {
  const { token } = useAuth()
  const { vods, isLoading, mutate } = useBroadcastDeviceVodList({
    broadcastDeviceId,
    broadcastType: type,
  })

  const [openDialog, setOpenDialog] = useState(false)
  const [selectedVod, setSelectedVod] = useState<BroadcastDeviceVod | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [vodToDelete, setVodToDelete] = useState<BroadcastDeviceVod | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUnbinding, setIsUnbinding] = useState(false)

  const handleUnbindTemplate = async () => {
    if (!broadcastDeviceId || !token) return
    setIsUnbinding(true)
    try {
      const { tvTemplateService } = await import('@/services/tv_template_service')
      const result = await tvTemplateService.unbindTemplate(broadcastDeviceId, token)
      if (result.success) {
        mutate()
        onSuccess?.()
      }
    } catch {
      toast.error('解除綁定失敗')
    } finally {
      setIsUnbinding(false)
    }
  }

  const handleAddVod = () => {
    setSelectedVod(null)
    setOpenDialog(true)
  }

  const handleEditVod = (vod: BroadcastDeviceVod) => {
    setSelectedVod(vod)
    setOpenDialog(true)
  }

  const handleDeleteVod = (vod: BroadcastDeviceVod) => {
    setVodToDelete(vod)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!vodToDelete || !token) return

    setIsDeleting(true)

    try {
      const result = await broadcastDeviceService.deleteBroadcastDeviceVod(
        vodToDelete.vod_id,
        token,
      )

      if (result.success) {
        toast.success(`已成功刪除影片 ${vodToDelete.vod_name}`)
        mutate()
        onSuccess?.()
      } else {
        toast.error(result.message || '無法刪除影片，請稍後再試')
      }
    } catch {
      toast.error('發生錯誤，請稍後再試')
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
      setVodToDelete(null)
    }
  }

  const getTypeLabel = () => {
    return type === BroadcastDeviceVodType.tv ? '電視影片' : '衛教影片'
  }

  return (
    <div className="space-y-6 w-full">
      <h2 className="text-2xl font-semibold tracking-tight">{getTypeLabel()}管理</h2>
      {templateId && templateName && (
        <div className="flex items-center justify-between bg-muted/50 border rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline">模板</Badge>
            <span className="text-sm">
              目前套用模板：<strong>{templateName}</strong>
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUnbindTemplate}
            disabled={isUnbinding}
          >
            {isUnbinding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            解除綁定
          </Button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">影片列表</h3>
        <Button onClick={handleAddVod} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          新增影片
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : vods.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">尚未新增任何{getTypeLabel()}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>影片名稱</TableHead>
              <TableHead>來源</TableHead>
              <TableHead>排序</TableHead>
              <TableHead>狀態</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vods.map((vod) => {
              const isFromTemplate = vod.template_id != null
              return (
                <TableRow key={vod.vod_id}>
                  <TableCell>{vod.vod_id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {vod.vod_name}
                      {isFromTemplate && (
                        <Badge variant="outline" className="text-xs">
                          來自模板
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{vod.source}</TableCell>
                  <TableCell>{vod.order ?? '-'}</TableCell>
                  <TableCell>{vod.is_active ? '啟用' : '停用'}</TableCell>
                  <TableCell className="text-right">
                    {isFromTemplate ? (
                      <Badge variant="secondary" className="text-xs">
                        模板
                      </Badge>
                    ) : (
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditVod(vod)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteVod(vod)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <VodDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        broadcastDeviceId={broadcastDeviceId}
        vod={selectedVod}
        broadcastType={type}
        onSuccess={() => {
          mutate()
          onSuccess?.()
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此影片嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原。這將永久刪除影片 &quot;{vodToDelete?.vod_name}&quot;。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                '確認刪除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
